import { z } from 'zod';

// Per-reviewer outcome within a single council run. Mirrors the orchestrator's
// (diff-vader/plugins/diff-vader/skills/dark-council/scripts/run-council.py)
// reviewer_status shape so we can persist + render it without re-shaping at
// the death-star edge.
//
// `timeout` and `error` were added 2026-05-13 after rewindio/external-user-service#30
// dropped its entire review when supportability hit the per-reviewer 180s
// timeout: the runner emits status='timeout', the schema rejected it, the whole
// artifact upload 400'd, and the PR ended up with no DV review. Schema-level
// acceptance is the first half of the fix; consumers (calibration rollup,
// portal) treat the new statuses as "didn't run successfully" — neither counts
// as a `runs` increment.
//
// `failed` was added 2026-05-14 (split from `skipped` upstream by diff-vader
// PR #52) — denotes a reviewer subprocess that crashed before producing
// output (exit non-zero with no JSON), distinct from `error` (which carries
// the connotation of an LLM/API-side failure) and `timeout` (which exceeded
// the per-reviewer wall clock). The schema-edge gap caused every artifact
// upload to 400 once any reviewer crashed — same failure mode as the
// rewindio/external-user-service#30 incident, just with a fifth literal.
export const CouncilReviewerStatusSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok') }),
  z.object({ status: z.literal('skipped'), reason: z.string() }),
  z.object({ status: z.literal('timeout'), reason: z.string() }),
  z.object({ status: z.literal('error'), reason: z.string() }),
  z.object({ status: z.literal('failed'), reason: z.string() })
]);
export type CouncilReviewerStatus = z.infer<typeof CouncilReviewerStatusSchema>;

// Per-reviewer wall clock and dispatch shape, from the orchestrator's
// `_build_timing_meta`. Every field is optional because the orchestrator
// records them opportunistically — `record_reviewer_timing(**fields)` merges
// whatever a given code path happens to know, so a chunked reviewer carries
// `batches` while an unchunked one doesn't, and only the Contrarian carries
// `findings_briefed`.
//
// `.passthrough()` is load-bearing, not laziness. The orchestrator adds timing
// fields freely (they arrive as **kwargs), and a closed schema here would strip
// each new one silently — which is the exact bug this whole change exists to
// fix. The declared keys are the ones we chart; the rest survive to DDB so the
// next field is queryable the day it ships rather than after someone notices a
// blank card.
export const CouncilReviewerTimingSchema = z
  .object({
    model: z.string().optional(),
    duration_s: z.number().nonnegative().optional(),
    failed: z.boolean().optional(),
    // Retry bookkeeping. `retried` marks an empty-completion retry that
    // succeeded (otherwise it looks free); `prose_retried` marks the
    // retry-on-prose path.
    retried: z.boolean().optional(),
    prose_retried: z.boolean().optional(),
    empty_responses: z.number().int().nonnegative().optional(),
    stop_reason: z.string().nullable().optional(),
    sdk_calls: z.number().int().nonnegative().optional(),
    // Chunked-dispatch shape. `batch_count`/`diff_bytes` are recorded at
    // planning time; `batches` and `batch_durations_s` only exist once at
    // least one batch has a duration, and the orchestrator emits them as a
    // pair.
    batch_count: z.number().int().nonnegative().optional(),
    diff_bytes: z.number().int().nonnegative().optional(),
    // Inner fields individually optional, mirroring diff-vader's
    // CouncilReviewerTimingSchema so the two sides stay identical on the wire.
    // `_summarize_batches` builds all four atomically today, but requiring them
    // is a bad trade on the producer side (a throw there fails the whole council
    // run and posts no review) and there is no reason for the two schemas to
    // disagree. Types stay validated, so genuine corruption is still rejected.
    batches: z
      .object({
        count: z.number().int().nonnegative().optional(),
        min_s: z.number().nonnegative().optional(),
        median_s: z.number().nonnegative().optional(),
        max_s: z.number().nonnegative().optional()
      })
      .passthrough()
      .optional(),
    batch_durations_s: z.array(z.number().nonnegative()).optional(),
    findings_emitted: z.number().int().nonnegative().optional(),
    findings_kept: z.number().int().nonnegative().optional(),
    // Contrarian only.
    findings_briefed: z.number().int().nonnegative().optional(),
    findings_total: z.number().int().nonnegative().optional()
  })
  .passthrough();
export type CouncilReviewerTiming = z.infer<typeof CouncilReviewerTimingSchema>;

// Run-level phase spans, under the reserved `_phases` key. Not derivable from
// the per-reviewer rows: `reviewers_span_s` is the wall clock of the whole
// fan-out, so `span - slowest reviewer` is the serialisation loss to the
// cache-warm barrier. That subtraction is the number #231 was measured on
// (562s span against a 369.8s slowest reviewer = 192s lost), and without the
// span persisted it can only be reconstructed from log timestamps by hand.
export const CouncilPhaseTimingSchema = z
  .object({
    reviewers_span_s: z.number().nonnegative().optional(),
    warm_barrier_capped: z.number().int().nonnegative().optional()
  })
  .passthrough();
export type CouncilPhaseTiming = z.infer<typeof CouncilPhaseTimingSchema>;

// The `meta.timing` map: reviewer name -> timing, plus the reserved `_phases`
// key carrying a DIFFERENT shape. `.catchall()` is what lets one object hold
// both — no reviewer name starts with an underscore, which is the invariant
// the orchestrator relies on to keep them in one map.
export const CouncilTimingSchema = z
  .object({ _phases: CouncilPhaseTimingSchema.optional() })
  .catchall(CouncilReviewerTimingSchema);
export type CouncilTiming = z.infer<typeof CouncilTimingSchema>;

// Per-finding record from the council. Mirrors the orchestrator's per-finding
// shape (diff-vader/packages/types/src/council.ts CouncilFindingSchema —
// that file is the source of truth; this one intentionally lags). If
// upstream adds a required field, this schema must be updated to match
// or ingest will reject artifacts. signal_id and signal_name are optional —
// only reviewers with numbered taxonomies emit them. signal_name carries the
// human-readable label (e.g. "Silent exception swallow") so the portal can
// render it without maintaining a parallel lookup table; the SKILL.md remains
// the single source of truth for what each ID means.
//
// `severity` was added 2026-08-12. Upstream has emitted it since the
// rewindio/membrane#1972 false positive (it is the *impact* axis, distinct
// from `confidence`, which is the reviewer's *certainty*), but this schema
// didn't declare it — and zod strips undeclared keys on parse, so every
// artifact ingested with severity landed in DDB without it. That blind spot
// cost ten weeks: when council-wide pct_substantive fell from 96.7% (2026-W22)
// to 21.1% (2026-W31), the suspect was run-council.py's
// `_clamp_low_severity_correctness` deterministic downgrade, but with severity
// dropped at ingest every stored finding read back as severity-less, so there
// was no way to tell from the dashboard whether the clamp fired at all. Kept a
// free string (not an enum) to mirror upstream exactly — an unrecognized value
// must round-trip verbatim rather than 400 the whole artifact upload, which is
// the same failure mode as the rewindio/external-user-service#30 incident
// above. Optional and nullable so historical artifacts and any runner build
// that doesn't emit it still validate.
//
// `carried_forward` was added 2026-08-17 for gh#326. Verifier mode re-emits
// every still-open prior finding on each re-review, so the same defect is
// counted once per review. #320 removed the within-day duplicates with an
// identity heuristic, but ~10% of them span days, and by the time
// aggregate-calibration-weekly runs it is summing daily integer counters —
// the individual findings are gone, so no heuristic can reach across the day
// boundary. The flag makes the re-emission self-describing instead of
// inferred, which works at every aggregation level.
//
// Declaring it here is necessary but NOT sufficient: the runner's output is
// first parsed against rewind-corp/diff-vader's own CouncilFindingSchema
// (packages/types/src/council.ts, applied at packages/runner/src/council.ts),
// and the artifact upload sends that *parsed* object. An undeclared key is
// therefore stripped upstream before death-star ever sees it. Both schemas
// must declare the field before run-council.py starts emitting it — the same
// ordering the `severity` blind spot above got wrong.
//
// Optional and nullable so every artifact already in DDB still validates; the
// rollup falls back to the identity heuristic when the flag is absent.
export const CouncilFindingSchema = z.object({
  reviewer: z.string(),
  file: z.string(),
  line: z.number().int().nonnegative(),
  message: z.string(),
  confidence: z.number().int().min(0).max(100),
  substantive: z.boolean(),
  signal_id: z.number().int().min(1).nullable().optional(),
  signal_name: z.string().nullable().optional(),
  severity: z.string().nullable().optional(),
  carried_forward: z.boolean().nullable().optional(),
});
export type CouncilFinding = z.infer<typeof CouncilFindingSchema>;

// The verdict inputs death-star slices artifacts on. Present on every real
// verdict; structurally absent on a Class-B deferral, where the runner's very
// first GitHub read failed and none of these were ever computed. Required-ness
// is therefore enforced per verdict state by the superRefine below rather than
// by `input` itself.
const CIArtifactInputSchema = z.object({
  policy_tier: z.enum(['LOW', 'MEDIUM', 'HIGH']),
  changed_files: z.array(z.string()),
  size_metrics: z.object({
    loc_changed: z.number().int().nonnegative(),
    files_changed: z.number().int().nonnegative()
  }),
  pr_author_is_human: z.boolean(),
  // hard_rules_violated: the path- and predicate-based policy hard rules this
  // diff matched. The runner has always sent it (it is a VerdictInput field)
  // and .passthrough() has always got it past parse — but nothing declared it,
  // so ingest-ci-artifact never cherry-picked it and it reached S3 and stopped.
  //
  // Declared now because the RR-95 audit found the F7 control it underpins was
  // not merely unsurfaced but UNQUERYABLE: the assessment claimed the portal
  // shows "verdict-vs-policy-tier outliers - if a PR was auto-approved despite
  // a hard_rule path being touched, that surfaces immediately", and the two
  // fields that question needs were absent from DynamoDB entirely. You cannot
  // ask the data a question it does not store. See death-star#369.
  //
  // Optional, not required: a Class-B deferral carries no `input` at all, and
  // pre-declaration rows legitimately lack it. Absent means "not recorded",
  // never "no hard rules matched" - an empty array is what says that.
  hard_rules_violated: z.array(z.string()).optional()
}).passthrough();

// CI artifact payload from the diff-vader runner
export const CIArtifactSchema = z.object({
  args: z.object({
    owner: z.string(),
    repo: z.string(),
    pull_number: z.number().int(),
    mode: z.enum(['shadow', 'enabled'])
  }),
  // Optional at the field level, then narrowed by verdict state in the
  // superRefine below: mandatory for a real verdict, omittable for DEFERRED.
  // Do NOT make this unconditionally required — that is the bug that 400'd
  // every Class-B deferral (rewindio/runbooks #362/#365/#368, 2026-08-25),
  // turning a graceful GitHub-outage deferral into a hard failure that paged
  // #diff-vader-ops and left the PR entirely unreviewed.
  input: CIArtifactInputSchema.optional(),
  verdict: z.object({
    state: z.enum(['APPROVE', 'NEEDS_HUMAN', 'REQUEST_CHANGES', 'TOO_LARGE', 'DEFERRED']),
    reason: z.string(),
    side_effects: z.record(z.string(), z.unknown())
  }),
  // Full findings array from the council. Optional for back-compat with
  // pre-Plan-2.6 runners that didn't include it. Empty array means the
  // council ran but emitted no findings (a valid output, e.g. PASS verdict
  // with nothing flagged). Absence (undefined) means the runner predates
  // this field — older artifacts in S3 may parse with findings undefined.
  findings: z.array(CouncilFindingSchema).optional(),
  cost: z
    .object({
      tokens_in: z.number().int().nonnegative(),
      tokens_out: z.number().int().nonnegative(),
      usd: z.number().nonnegative(),
      // Cache-aware breakdown of the summed tokens_in (input + cache reads +
      // cache writes). Present only on artifacts from the SDK dispatch path
      // (DV_DISPATCH=sdk); the CLI path emits only the summed tokens_in, so
      // these are optional and absent on historical / CLI-path rows.
      input_tokens: z.number().int().nonnegative().optional(),
      cache_read_input_tokens: z.number().int().nonnegative().optional(),
      cache_creation_input_tokens: z.number().int().nonnegative().optional(),
      per_reviewer: z.record(z.string(), z.object({
        tokens_in: z.number().int().nonnegative(),
        tokens_out: z.number().int().nonnegative(),
        usd: z.number().nonnegative(),
        input_tokens: z.number().int().nonnegative().optional(),
        cache_read_input_tokens: z.number().int().nonnegative().optional(),
        cache_creation_input_tokens: z.number().int().nonnegative().optional()
      }))
    })
    .optional(),
  model_versions: z.record(z.string(), z.string()).optional(),
  // `meta.reviewer_status`: per-reviewer ran/skipped outcome. Optional for
  // back-compat with runner versions that pre-date Plan 2.4b. Passthrough
  // record-of-unknown for any other meta keys we haven't typed yet. `.nullish`
  // (not `.optional`) tolerates a runner that emits `"meta": null` rather
  // than omitting the key — without this, a 400 ingest failure would be the
  // first signal of that drift.
  // A3b: policy resolution outcome for the org+repo on this PR. Optional
  // for back-compat with pre-A3b runners. death-star ingests this onto the
  // OPTIN row (org axis) and the REPO CONFIG row (repo axis) so the config
  // UI can render the customized / inherited / synthesized badge and the
  // "Show orgs needing policy audit" filter.
  policy_source: z
    .object({
      org: z.enum(['central', 'synthesized']),
      repo: z.enum(['central', 'inherited', 'synthesized']),
    })
    .optional(),
  meta: z
    .object({
      reviewer_status: z.record(z.string(), CouncilReviewerStatusSchema).optional(),
      // Wall-clock milliseconds of the council orchestration window measured
      // by the runner (around invokeCouncil, exclusive of workflow setup).
      // Optional for back-compat with pre-duration runner builds.
      // `.int()` rejects Infinity, NaN, and fractional ms — the runner rounds
      // before sending, but the schema is the boundary that protects DDB
      // from a regressed/mocked sender.
      duration_ms: z.number().int().nonnegative().optional(),
      // Per-reviewer wall clock plus run-level phase spans. `duration_ms` above
      // answers "was this run slow"; this answers "which part of it was", which
      // is the question you cannot reconstruct after the fact.
      //
      // Declared explicitly rather than left to the `.passthrough()` below for
      // the reason the cost-telemetry block further down records: a passthrough
      // key validates but is never persisted, so 0/663 artifacts in a 30d
      // window carried `short_circuit_reason` in DDB while the runner had been
      // sending it the whole time. The orchestrator has been emitting `timing`
      // and saying so in a comment ("until the consumer schema accepts it this
      // is dropped downstream") since #215.
      timing: CouncilTimingSchema.optional(),
      // PR context — populated by the diff-vader runner from octokit.pulls.get.
      // Optional for back-compat with pre-PR-2 emitters; renders as `—` in the
      // portal until populated.
      title: z.string().optional(),
      author: z.string().optional(),
      branch: z.string().optional(),
      // Per-finding review-comment IDs + body excerpts for the polling
      // reaction-ingest Lambda. Each entry's body_excerpt is up to 500 chars
      // (post-marker), which the polling Lambda persists onto reaction rows
      // for the clusterer's keyword extraction. Reviewer is captured here so
      // we don't have to re-parse the marker on the death-star side.
      review_comments: z
        .array(z.object({
          id: z.number().int(),
          reviewer: z.string(),
          body_excerpt: z.string(),
          // Populated by poll-reactions after it queries GitHub for the
          // comment's anchor: true = the line was modified before merge
          // (acted on), false = anchor still intact (merged through), null =
          // indeterminate (off-diff at posting, or the comment 404'd).
          acted_on: z.boolean().nullable().optional(),
          acted_on_checked_at: z.string().optional(),
          // Populated by ingest-ci-artifact (Phase 2.4b) when a subsequent
          // re-review artifact's verifier_results.by_finding_id flips this
          // comment_id to 'resolved'. Latched true; never written false.
          // Calibration rollup OR's this with acted_on. null/undefined =
          // no verifier signal yet (excluded from denominator alone; only
          // contributes when paired with acted_on).
          acted_on_verifier: z.boolean().nullable().optional(),
          acted_on_verifier_checked_at: z.string().optional()
        }))
        .optional(),
      // review_kind: distinguishes initial PR review from re-review on same PR.
      // Drives portal badge + Phase B/C shadow-window metric splits. Optional for
      // back-compat with pre-verifier-mode artifacts.
      review_kind: z.enum(['initial', 're-review']).optional(),
      // trigger: user-initiated (today's only path, slash-command) vs auto-triggered
      // (reserved for a future push-on-PR auto-trigger). Optional for back-compat.
      trigger: z.enum(['user-initiated', 'auto-triggered']).optional(),
      // Cost-optimization telemetry (Plan 2026-05-28, F1/F3). All optional —
      // each is emitted by the runner only on the specific path that produces
      // it, so presence is the signal. Declared explicitly (rather than left
      // on the meta object's .passthrough()) so ingest-ci-artifact can persist
      // them to DDB for the cost dashboard's F3-short-circuit and F1-adoption
      // charts. Before this, 0/663 artifacts in a 30d window had any of these
      // fields in DDB despite the runner sending short_circuit_reason on the
      // wire.
      //   short_circuit_reason: which gate skipped the council. F3's value is
      //     'predetermined_needs_human_by_size'; other values come from the
      //     pre-existing size_too_large / hard_rules_always_human /
      //     external_contributor short-circuits.
      //   verifier_mode_no_prior_findings_fallthrough: the calibration-miss-
      //     freeze fallthrough fired (verifier mode → fresh council).
      //   delta_payload_used: F1 actually sent a delta diff on the fallthrough
      //     path (true) vs fell back to full diff (false). Absent off the
      //     fallthrough path entirely.
      //   short_circuit_bypassed: a user slash-command suppressed the
      //     hard_rules_always_human short-circuit (DV_USER_OVERRIDE).
      short_circuit_reason: z.string().optional(),
      verifier_mode_no_prior_findings_fallthrough: z.boolean().optional(),
      delta_payload_used: z.boolean().optional(),
      short_circuit_bypassed: z.boolean().optional(),
      // auto_merge: outcome of the runner's enablePullRequestAutoMerge call
      // (diff-vader#265 / #267). Declared explicitly for the same reason as the
      // block above — the meta object's .passthrough() gets it past parse, but
      // ingest-ci-artifact cherry-picks keys, so without a declaration it would
      // reach S3 and never reach DDB.
      //
      // ABSENCE IS MEANINGFUL AND IS NOT THE SAME AS `enabled: false`. The
      // runner emits this only when the verdict actually asked for auto-merge
      // and the run wasn't in shadow mode. Absent = never attempted; present
      // with enabled:false = attempted and GitHub refused. Only the second says
      // anything about the App's permissions, so never default this to false.
      //
      // Why it is worth a column: the `diff-vader` App is installed with
      // `contents: read` and enablePullRequestAutoMerge requires
      // `contents: write`, so every attempt 403s and the runner swallows it.
      // That accidental scope — not any author-type check in the verdict
      // engine — is what has kept a human on every bot-authored auto-approved
      // merge (SOC 2 CC8.1.2, RR-95). The value is uniformly
      // {enabled:false,status:403} today; the first `enabled: true` is the
      // signal that the separation-of-duties posture changed.
      auto_merge: z
        .object({
          enabled: z.boolean(),
          error: z.string().optional(),
          // 403 = the missing `contents: write` scope. Other statuses are
          // genuine GitHub failures (or absent when none was supplied).
          status: z.number().int().optional(),
        })
        .optional(),
      // auth_mode: which credential actually posted the review for this run.
      //
      // Declared AHEAD of the runner emitting it, deliberately. death-star is
      // the receiving half of a two-repo change and the ingest handler
      // cherry-picks meta keys, so a field the runner sends before death-star
      // declares it is silently dropped - that has now happened three times in
      // this pipeline (short_circuit_reason: 0/663 artifacts carried it in a
      // 30d window despite the runner sending it on the wire; meta.auto_merge
      // reached S3 but not DynamoDB until #372). Landing the receiver first
      // makes diff-vader#272 a one-sided change instead of a coordinated one.
      //
      // Why it matters, and why it is evidence rather than telemetry: the
      // runner posts approvals with the `diff-vader-bot` PAT, and on a 401 it
      // falls back to App auth. An App-authored APPROVE does NOT count toward
      // `required_approving_review_count`, so a lapsed PAT presents as "Diff
      // Vader got stricter" rather than as a credential failure, and today the
      // swap is recorded only in a console.warn. Without this field death-star
      // cannot say WHO approved a PR - which is exactly the question SOC 2
      // CC8.1.2 asks. See RR-95 and diff-vader#269 (the PAT's next expiry is
      // approximately 2026-11-11).
      //
      // Absence means "this runner build does not report it", never "PAT".
      // Do not default it.
      auth_mode: z.enum(['pat', 'app']).optional(),
      // stale_approval_revocation: outcome of the runner revoking Diff Vader's
      // own earlier APPROVE after a later run reached a non-APPROVE verdict
      // (diff-vader#272 / #336). Declared for the same reason as auto_merge:
      // without it the value reaches S3 and never reaches DynamoDB.
      //
      // ABSENCE IS MEANINGFUL. The runner emits this only when a revocation
      // was attempted (enabled mode, a non-APPROVE / non-DEFERRED verdict, and
      // at least one live DV approval). Absent = nothing to revoke, never
      // "revoked". Anything but `revoked` means the PR may still read as
      // approved against a verdict that says otherwise:
      //   revoked   - every stale approval was dismissed.
      //   forbidden - GitHub 403'd at least one dismissal; an approval stands.
      //   failed    - some other failure (5xx, 404, 422, network) left one
      //               standing.
      //
      // `outcome` is a strict enum, like auth_mode: a new outcome value must
      // land here BEFORE the runner emits it, or the whole artifact is
      // rejected.
      //
      // The counts are checked against the outcome, because this is an audit
      // record and a self-contradicting one (a `revoked` with an approval
      // still standing) would misreport exactly the case it exists to catch.
      // The runner builds these by construction (dismissStaleDvApprovals: each
      // attempted id is either dismissed or pushed to survivingIds, and
      // `revoked` is emitted iff none survived), so a real runner cannot trip
      // them:
      //   - attempted >= 1 (absent, not zero, when nothing was attempted)
      //   - dismissed + surviving_review_ids.length === attempted
      //   - revoked  <=> no surviving ids
      stale_approval_revocation: z
        .object({
          outcome: z.enum(['revoked', 'forbidden', 'failed']),
          attempted: z.number().int().positive(),
          dismissed: z.number().int().nonnegative(),
          // Review ids still standing. Absent when outcome is `revoked`.
          surviving_review_ids: z.array(z.number().int()).optional(),
          // HTTP status of the last failed dismissal, when GitHub supplied one.
          status: z.number().int().optional(),
        })
        .superRefine((r, ctx) => {
          const surviving = r.surviving_review_ids?.length ?? 0;
          if (r.dismissed + surviving !== r.attempted) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['dismissed'],
              message: `dismissed (${r.dismissed}) + surviving_review_ids (${surviving}) must equal attempted (${r.attempted})`,
            });
          }
          if ((r.outcome === 'revoked') !== (surviving === 0)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['outcome'],
              message: r.outcome === 'revoked'
                ? 'outcome "revoked" cannot have surviving_review_ids'
                : `outcome "${r.outcome}" requires at least one surviving_review_id`,
            });
          }
        })
        .optional(),
      // verifier_results: per-finding classification summary emitted when the
      // orchestrator ran in verifier mode. Drives the pr-detail breakdown row
      // and the daily-monitoring resolved/unresolved-rate metrics. Absent for
      // initial reviews and for `--fresh` full re-reviews.
      //
      // `refuted` is the fifth status, and this schema had to accept it BEFORE
      // the orchestrator could emit it (rewind-corp/engineering-plugins#291).
      // The enum is strict, so an unknown status value does not get stripped
      // the way an unknown key would — it fails the parse, and death-star then
      // rejects the whole artifact, losing the calibration record this field
      // exists to keep. Hence: this repo first, then diff-vader's runner
      // schema, then the emitter.
      //
      // The `refuted` COUNT is optional for the mirror-image reason. The other
      // four are required, so declaring a fifth required count would reject
      // every artifact from a runner that predates the emitter change — which
      // is every artifact written until that PR lands, and all historical ones
      // forever. Read it as `?? 0`.
      verifier_results: z
        .object({
          total_prior_findings: z.number().int().nonnegative(),
          resolved: z.number().int().nonnegative(),
          unresolved: z.number().int().nonnegative(),
          partial: z.number().int().nonnegative(),
          withdrawn: z.number().int().nonnegative(),
          refuted: z.number().int().nonnegative().optional(),
          by_finding_id: z.record(
            z.string(),
            z.object({
              status: z.enum(['resolved', 'unresolved', 'partial', 'withdrawn', 'refuted']),
              rationale: z.string(),
            })
          ),
        })
        .optional(),
      // `meta.findings_anchor_status`: per-finding anchor outcome (anchored to a `+`
      // line vs dropped because the claimed line wasn't in the diff). Optional for
      // back-compat with pre-Phase-0 runners that didn't compute it. Counters are
      // non-negative ints; `by_reviewer` is sparse — only reviewers that emitted at
      // least one finding have an entry. See diff-vader#79.
      findings_anchor_status: z
        .object({
          total: z.number().int().nonnegative(),
          anchored: z.number().int().nonnegative(),
          dropped_unanchored: z.number().int().nonnegative(),
          by_reviewer: z.record(
            z.string(),
            z.object({
              total: z.number().int().nonnegative(),
              anchored: z.number().int().nonnegative(),
              dropped_unanchored: z.number().int().nonnegative(),
            })
          ),
          // Per-finding diagnostic data for hypothesis A/B classification of
          // anchor failures. Optional for back-compat with pre-#79-followup
          // runners. Modeled as a discriminated union on `drop_reason` so the
          // (reason, claimed_line) invariants are enforced at the schema
          // boundary instead of relying on runner-side discipline:
          //   'malformed_line'   — runner-side `isAnchorable` filter caught a
          //                        genuinely bad shape (negative/non-int line or
          //                        empty file). claimed_line MUST be null. Not
          //                        emitted to GH.
          //   'file_level'       — the reviewer flagged a whole-file concern with
          //                        `line: 0` (a value the council schema legally
          //                        permits, minimum: 0). Not anchorable to a diff
          //                        position, so the runner records it here rather
          //                        than as an inline-anchored finding. claimed_line
          //                        MUST be null. Distinct from malformed_line so
          //                        this telemetry doesn't read expected file-level
          //                        findings as a parse regression. (How the runner
          //                        then renders it on the PR is its concern, not
          //                        this receiver's.) See diff-vader#79 followup.
          //   'not_actionable'   — runner-side `isActionable` filter caught it
          //                        (advisory "no concerns" pattern match). The
          //                        finding was anchorable but suppressed before
          //                        anchor consideration. claimed_line is the
          //                        valid positive int the reviewer emitted.
          //   'outside_diff'     — anchorable + actionable, but the line is not
          //                        in the PR's diff (reviewer reasoned over
          //                        unchanged code). Caught before posting
          //                        (diff-vader#79 Phase 1) so it never rejects
          //                        the batch; the runner body-lists it instead.
          //                        claimed_line is the positive int the
          //                        reviewer emitted.
          //   'gh_rejected_batch'— inline createReview failed because at least
          //                        one comment in the batch had a line outside
          //                        the diff context. All findings in the
          //                        rejected batch are recorded even though
          //                        only some were the cause. claimed_line is
          //                        the positive int the reviewer emitted.
          //   'hunk_header_unparseable'
          //                      — anchorable + actionable, but the finding's
          //                        line sits in a file whose `@@` hunk header the
          //                        runner could not parse, so it had no anchors to
          //                        check against. A runner-side parse failure,
          //                        NOT the reviewer reasoning over unchanged code,
          //                        so it must never be counted as outside_diff.
          //                        claimed_line is the positive int the reviewer
          //                        emitted. Accepted here before the runner emits
          //                        it (diff-vader#272): an unknown drop_reason
          //                        rejects the whole artifact.
          // See diff-vader#79.
          dropped_details: z
            .array(
              z.discriminatedUnion('drop_reason', [
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.null(),
                  drop_reason: z.literal('malformed_line'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.null(),
                  drop_reason: z.literal('file_level'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.number().int().positive(),
                  drop_reason: z.literal('not_actionable'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.number().int().positive(),
                  drop_reason: z.literal('outside_diff'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.number().int().positive(),
                  drop_reason: z.literal('gh_rejected_batch'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.number().int().positive(),
                  drop_reason: z.literal('hunk_header_unparseable'),
                }),
              ])
            )
            .optional(),
        })
        .optional()
    })
    .passthrough()
    .nullish(),
  ts: z.string()
}).superRefine((artifact, ctx) => {
  // `input` is mandatory for every real verdict — dropping it would silently
  // strip the slicing dimensions (path category, size bucket, change type)
  // off the artifact, so a runner regression must fail loudly at the boundary
  // rather than land a dimensionless row. DEFERRED is the one state that
  // legitimately has no input: the deferral exists precisely because the read
  // that would have produced it failed.
  if (artifact.verdict.state !== 'DEFERRED' && artifact.input === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['input'],
      message: `input is required for verdict.state="${artifact.verdict.state}" (omittable only when state="DEFERRED")`,
    });
  }
});
export type CIArtifact = z.infer<typeof CIArtifactSchema>;

// SQS message enqueued onto ReDispatchQueue when a DEFERRED artifact is
// ingested (transient upstream outage). ingest-ci-artifact enqueues attempt 1
// with DelaySeconds=120; ReDispatchWorkerFn self-re-enqueues attempts 2..5
// (backoff 300 / 900 / 1800 / 3600s) and stops at the cap. See design section I.
//
// head_sha is nullable: present when the runner captured meta.head_sha
// (Class A — Anthropic overload), null when it couldn't (Class B — GitHub
// fetch failed). The worker resolves a null head_sha via octokit and falls
// back to a pull_number-only ledger key if GitHub is also unreachable.
export const ReDispatchJobSchema = z.object({
  owner: z.string().min(1),
  repo: z.string().min(1),
  pull_number: z.number().int().positive(),
  head_sha: z.string().min(1).nullable(),
  // Verdict mode carried from the deferred artifact's args.mode. The worker
  // re-checks getEffectiveDvConfig and dispatches with the fresh config mode;
  // this value is a fallback + observability aid.
  mode: z.enum(['shadow', 'enabled']),
  // 1-based re-dispatch attempt this message represents. Ingest always enqueues
  // attempt 1; the worker enqueues 2..5. Cap = 5 re-dispatches (attempt 0 is
  // the original deferred run), 6 total runs. Must track
  // MAX_REDISPATCH_ATTEMPTS in redispatch-worker.ts — a lower max here DLQs
  // the chain's last attempts on parse.
  attempt: z.number().int().min(1).max(5),
  // Upstream failure classification from meta.failure_class, carried through so
  // the exhaustion Slack page can name the outage. Kept as a free-form string
  // for forward-compat with future classes. Non-empty: the ops page interpolates
  // this directly into the Slack message text, so an empty/whitespace value
  // would render as a blank outage name.
  failure_class: z.string().min(1),
  // Absolute instant before which dispatching is pointless, from the runner's
  // meta.retry_after_iso (diff-vader#300). Set when the deferral was a GitHub
  // rate limit: the runner reads the reset out of the response headers, so it
  // knows the exact moment the budget returns — a fact death-star cannot derive
  // on its own.
  //
  // ABSOLUTE, not a duration, deliberately: the artifact can be ingested well
  // after the runner emitted it, and a relative "wait 36 minutes" would restart
  // from ingest time.
  //
  // Waiting for this instant must NOT consume an attempt — see WAIT-HOP in
  // redispatch-worker.ts. Nullish for every other deferral class, which keeps
  // the pre-existing backoff behaviour and lets messages already in flight
  // (written before this field existed) parse unchanged.
  not_before: z.string().datetime().nullish(),
  // How many times this job has been re-enqueued purely to wait out `not_before`.
  // Distinct from `attempt`: SQS caps DelaySeconds at 900s, so a window tens of
  // minutes out needs several hops to reach, and none of them are dispatches.
  // Bounded so a bad/far-future `not_before` cannot re-enqueue forever.
  wait_hops: z.number().int().min(0).max(16).optional(),
  // The original dispatch's reviewer/provenance args, carried so a recovered
  // dispatch asks for the SAME review that was lost.
  //
  // These exist for the dispatch-exhaustion producer (death-star#238): a
  // `/diff-vader-review security` whose workflow_dispatch died on a transient
  // 500 must come back as `security`, not as a plain council run. Recovering
  // into a quietly different review is worse than the failure — the requester
  // gets something that looks like their answer and is not.
  //
  // Defaulted, not required, so messages already in flight when this deploys
  // (the DEFERRED path enqueues with up to 900s of delay) keep parsing. The
  // defaults are also exactly the DEFERRED path's own semantics: that path
  // re-runs the whole council with no extras, which is what it has always done.
  extra_reviewers: z.string().default(''),
  human_requested: z.boolean().default(false),
  user_override: z.boolean().default(false),
  // `/diff-vader-review --fresh` makes the runner skip verifier mode and do a
  // full re-scan. Same reasoning as the fields above: recovering a --fresh
  // request as a verifier run answers a different question than the one asked.
  fresh: z.boolean().default(false),
  // Which producer enqueued this job. The two have genuinely different
  // semantics in the worker's stop conditions, and keying on `failure_class`
  // instead would be keying behaviour on a free-form telemetry string.
  //
  // 'deferred_artifact' — a review RAN and deferred. An artifact exists, a
  //   push would have re-triggered DV, and both stop conditions are correct
  //   as originally written.
  // 'dispatch_exhausted' — the review never started (death-star#238). Any
  //   artifact on the PR predates this failure, and under `rerunOnPush: off`
  //   a push does NOT re-trigger, so both stop conditions need adjusting or
  //   the recovery silently no-ops.
  //
  // Defaulted so in-flight pre-#238 messages keep the DEFERRED behaviour.
  origin: z.enum(['deferred_artifact', 'dispatch_exhausted']).default('deferred_artifact'),
  // When the recovery was armed, ISO-8601. Lets the worker tell a verdict
  // produced AFTER this failure (real recovery — stop) from one that merely
  // predates it (not recovery — keep going). Only set by the
  // dispatch_exhausted producer; absent on DEFERRED jobs, which keep the
  // original "any non-DEFERRED verdict stops the chain" rule.
  enqueued_at: z.string().datetime().nullish(),
});
export type ReDispatchJob = z.infer<typeof ReDispatchJobSchema>;

export const PagerDutyIncidentSchema = z.object({
  event: z.object({
    event_type: z.string(),
    occurred_at: z.string(),
    data: z.object({
      id: z.string(),
      summary: z.string(),
      description: z.string().optional(),
      html_url: z.string()
    })
  })
});
export type PagerDutyIncident = z.infer<typeof PagerDutyIncidentSchema>;

export const ConfluencePageSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.object({ storage: z.object({ value: z.string() }) }),
  version: z.object({ when: z.string() }),
  _links: z.object({ webui: z.string() }).optional()
});
export type ConfluencePage = z.infer<typeof ConfluencePageSchema>;

// Incident-linkage schemas. The IncidentRecordSchema is the canonical row
// shape stored under an INCIDENT# pk. Links are stored under PR# pks with
// k.incidentLink SKs (see keys.ts). TopologySnapshot is the singleton
// rewind-topology view used by the linker for service → arch_group resolution.
export const IncidentRecordSchema = z.object({
  pk: z.string(),
  sk: z.literal('META'),
  source: z.enum(['confluence-postmortem', 'pagerduty-statuspage']),
  source_url: z.string().url(),
  title: z.string(),
  arch_group: z.enum(['edge', 'engine', 'hero']).nullable(),
  impacted_business_services: z.array(z.string()).default([]),
  occurred_at: z.string().datetime(),
  resolved_at: z.string().datetime().nullable(),
  severity: z.string().nullable(),
  referenced_pd_alarm_url: z.string().url().nullable(),
  ingested_at: z.string().datetime()
});
export type IncidentRecord = z.infer<typeof IncidentRecordSchema>;

// Why a PR was linked to an incident, strongest signal first. Ordering is
// meaningful: incident-linkage.ts maps each member to a fixed confidence
// score. This is the single declaration of the taxonomy — the linker, the DDB
// read path, the API handlers and the portal's reason labels all derive from
// it, so adding a member here is what makes the exhaustiveness checks
// downstream fail until every consumer handles it (issue #315).
export const IncidentLinkReasonSchema = z.enum([
  'explicit_pr_url',
  'explicit_pr_shorthand',
  'business_service_match_via_status_page',
  'service_match_via_pm_component',
  'arch_group_only',
  'timing_only'
]);
export type IncidentLinkReason = z.infer<typeof IncidentLinkReasonSchema>;

// Reverse-index row under a PR# pk listing every incident touching that PR.
// org/repo/pr_number are encoded in pk via k.pr(); incident_id encodes which
// incident this link points to and de-dupes the row from sibling links.
export const IncidentLinkSchema = z.object({
  pk: z.string(),                                  // k.pr(org, repo, pr_number)
  sk: z.string(),                                  // k.incidentLink(incident_id)
  incident_id: z.string(),                         // e.g. 'pm:4483678214', 'sp:PWYRQT0'
  confidence: z.number().int().min(0).max(100),
  reason: IncidentLinkReasonSchema,
  linked_at: z.string().datetime()
});
export type IncidentLink = z.infer<typeof IncidentLinkSchema>;

export const TopologySnapshotSchema = z.object({
  pk: z.literal('TOPOLOGY#current'),
  sk: z.literal('SNAPSHOT'),
  services: z.record(z.string(), z.object({ repo: z.string(), arch: z.enum(['edge', 'engine', 'hero']) })),
  arch_groups: z.object({
    edge: z.array(z.string()),
    engine: z.array(z.string()),
    hero: z.array(z.string())
  }),
  synced_at: z.string().datetime()
});
export type TopologySnapshot = z.infer<typeof TopologySnapshotSchema>;

export const DvMode = z.enum(['disabled', 'shadow', 'enabled']);
export type DvMode = z.infer<typeof DvMode>;

export const DiffVaderConfigSchema = z.object({
  mode: DvMode,
  rerunOnPush: z.boolean(),
});
export type DiffVaderConfig = z.infer<typeof DiffVaderConfigSchema>;

const ConfigChangeEvent = z.object({
  event_type: z.literal('dv_config_change'),
  scope: z.enum(['org_default', 'repo_override']),
  org: z.string(),
  repo: z.string().optional(),
  before: DiffVaderConfigSchema.nullable(),
  after: DiffVaderConfigSchema.nullable(),
}).superRefine((data, ctx) => {
  if (data.scope === 'repo_override' && !data.repo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'repo is required when scope is "repo_override"',
      path: ['repo'],
    });
  }
  if (data.scope === 'org_default' && data.repo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'repo must be omitted when scope is "org_default"',
      path: ['repo'],
    });
  }
});

const OrgOptedInEvent = z.object({
  event_type: z.literal('org_opted_in'),
  org: z.string(),
});

const OrgOptedOutEvent = z.object({
  event_type: z.literal('org_opted_out'),
  org: z.string(),
  cascade_deleted_count: z.number().int().nonnegative(),
});

const RedundantCleanedEvent = z.object({
  event_type: z.literal('redundant_overrides_cleaned'),
  org: z.string(),
  removed_count: z.number().int().nonnegative(),
  removed_repos: z.array(z.string()),
});

const RbacGrantEvent = z.object({
  event_type: z.literal('rbac_grant'),
  target_email: z.string(),
  old_role: z.union([z.literal('viewer'), z.literal('triager'), z.literal('superAdmin')]).nullable(),
  new_role: z.union([z.literal('triager'), z.literal('superAdmin')]),
});

const RbacRevokeEvent = z.object({
  event_type: z.literal('rbac_revoke'),
  target_email: z.string(),
  old_role: z.union([z.literal('triager'), z.literal('superAdmin')]),
});

const UserLoginEvent = z.object({
  event_type: z.literal('user_login'),
  target_email: z.string(),
});

// Supplementary audit trail entry for a quarterly ISO 42001 Clause 9.1
// sign-off (Task 5). This is deliberately NOT the evidence of record — that
// is the SIGNOFF#<org> / SIGNOFF#<org>#<signed_at> rows under
// QUARTER#<quarter> (see QuarterlySignoffRowSchema above). recordEvent()
// swallows write failures by design, which is correct for this supplementary
// row and would be wrong for the attestation itself.
const QuarterlySignoffEvent = z.object({
  event_type: z.literal('quarterly_signoff'),
  quarter: z.string(),
  org: z.string(),
  revision: z.number().int().positive(),
});

// Supplementary audit trail entry for a quarterly ISO 42001 Clause 9.1
// review note (Task 6). Like QuarterlySignoffEvent above, this is NOT the
// evidence of record — that is the NOTE#<org>#<at>#<id> row (see
// QuarterlyNoteRowSchema below). recordEvent() swallows write failures by
// design, which is correct for this supplementary row and would be wrong
// for the note itself.
const QuarterlyNoteEvent = z.object({
  event_type: z.literal('quarterly_note'),
  quarter: z.string(),
  org: z.string(),
  // The `<id>` component of the note's own SK (NOTE#<org>#<at>#<id>).
  // QuarterlySignoffEvent above carries `revision`, which lets an auditor
  // locate the exact signing this entry refers to; without an equivalent here
  // a note audit entry names only (quarter, org) — hundreds of possible rows —
  // and an audit-trail entry that cannot be traced to its row without scanning
  // the partition is not usable as evidence. The handler already has the id at
  // the moment it builds the SK.
  note_id: z.string(),
});

// Supplementary audit trail entry for a manual chart annotation. The row of
// record is the ANNOTATION# row (AnnotationRowSchema below); `annotation_id`
// is the <id> component of its SK, for the same traceability reason as
// QuarterlyNoteEvent.note_id. Auto-detected model_change annotations are
// written by the daily aggregator, not by a person, and emit no audit entry.
const AnnotationCreatedEvent = z.object({
  event_type: z.literal('annotation_created'),
  annotation_id: z.string(),
  effective_date: z.string(),
  reviewer: z.string(),
});

export const AuditEventSchema = z.union([
  ConfigChangeEvent,
  OrgOptedInEvent,
  OrgOptedOutEvent,
  RedundantCleanedEvent,
  RbacGrantEvent,
  RbacRevokeEvent,
  UserLoginEvent,
  QuarterlySignoffEvent,
  QuarterlyNoteEvent,
  AnnotationCreatedEvent,
]);
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const PolicyVersionSchema = z.object({
  file_path: z.string(),
  commit_sha: z.string(),
  short_sha: z.string(),
  pushed_at: z.string(),
  author_login: z.string(),
  author_email: z.string(),
  commit_message: z.string(),
  pr_number: z.number().int().nullable(),
  pr_url: z.string().nullable(),
  content_sha256: z.string(),
  content_s3_key: z.string(),
  content_size: z.number().int().nonnegative(),
});
export type PolicyVersion = z.infer<typeof PolicyVersionSchema>;

export const SliceMetricSchema = z.enum([
  'auto_approval',
  'ttm',
  'ttfr',
  'revert_rate',
  'override_rate',
  'council_wall_clock',
  // Finding-quality metrics (diff-vader#241). The six above measure what
  // happened to the PR — was it auto-approved, how fast did it merge, was it
  // reverted. None of them measure what the council actually SAID about the
  // diff, so "did review quality hold at 1500-3000 LOC" was unanswerable and
  // the auto-approve caps rested on judgement.
  //
  // Denominators differ per metric and are NOT interchangeable:
  //   findings_per_run  — denominator is council RUNS (numerator: findings)
  //   substantive_share — denominator is FINDINGS     (numerator: substantive)
  //   severity_mix      — denominator is FINDINGS     (numerator: high+critical)
  'findings_per_run',
  'substantive_share',
  'severity_mix',
]);
export type SliceMetric = z.infer<typeof SliceMetricSchema>;

export const SliceDimensionSchema = z.enum([
  'size_bucket',
  'path_category',
  'change_type',
  'author_type',
  'primary_language',
]);
export type SliceDimension = z.infer<typeof SliceDimensionSchema>;

export const SliceRollupRowSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  org: z.string(),
  repo: z.string(),
  day: z.string(),
  metric: SliceMetricSchema,
  dimension: SliceDimensionSchema,
  bucket_counts: z.record(z.string(), z.number().int().nonnegative()),
  bucket_numerators: z.record(z.string(), z.number().int().nonnegative()),
  ttm_sum_seconds: z.record(z.string(), z.number().int().nonnegative()).optional(),
  ttm_count: z.record(z.string(), z.number().int().nonnegative()).optional(),
  ttfr_sum_seconds: z.record(z.string(), z.number().int().nonnegative()).optional(),
  ttfr_count: z.record(z.string(), z.number().int().nonnegative()).optional(),
  // Per-bucket samples for the council_wall_clock metric. Each value is a
  // duration_ms (council end-to-end wall-clock) for one CI artifact. Stored as
  // an array (not sum+count) because the metric is reported as p50/p95, which
  // cannot be reconstructed from sum+count. Typical storage ~400 bytes / day;
  // worst case ~8KB / day for hot repos. Optional so existing rows parse
  // unchanged.
  council_duration_ms_samples: z.record(z.string(), z.array(z.number().int().nonnegative())).optional(),
  // Per-bucket severity histogram for the severity_mix metric: bucket ->
  // severity key -> count. Severity keys are whatever severityBucket() in
  // calibration-rollup.ts produces (lowercased + trimmed), plus the reserved
  // 'unspecified' for findings that carried no severity at all.
  //
  // Stored as a nested record rather than folded into bucket_numerators because
  // a MIX is not a rate: bucket_numerators carries only the high+critical share
  // (the headline scalar) and would throw the composition away. Same precedent
  // as council_duration_ms_samples above — one optional field, read by one
  // metric.
  //
  // 'unspecified' is a first-class bucket, never dropped and never folded into
  // 'low'. Every finding stored before 2026-08-12 reads back unspecified
  // because CouncilFindingSchema didn't declare `severity` and zod strips
  // undeclared keys at ingest, so a high unspecified share on older data is a
  // schema artifact, not a measurement. Optional so existing rows parse
  // unchanged.
  severity_counts: z.record(z.string(), z.record(z.string(), z.number().int().nonnegative())).optional(),
  computed_at: z.string().datetime(),
  // Set on SLICE_ROLLUP_ALL#<day> rows only — count of (org, repo) pairs whose
  // per-repo rows were summed into this all-orgs row. Observability only.
  n_repos_contributed: z.number().int().nonnegative().optional(),
});
export type SliceRollupRow = z.infer<typeof SliceRollupRowSchema>;

// CloudWatch alarm notification row, stored under k.alarmLog (month-partitioned).
// `kind` distinguishes a real alarm state transition from the monthly heartbeat
// row that proves the notification path itself is alive. `delivered` +
// `delivery_error` record the Slack-post outcome so a silent notifier failure
// is visible in the audit trail rather than assumed from the alarm having
// fired. No expires_at_epoch: these rows are audit evidence and must not TTL.
export const AlarmNotificationRowSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  kind: z.enum(['alarm_transition', 'heartbeat']),
  alarm_name: z.string(),
  old_state: z.string(),
  new_state: z.string(),
  reason: z.string(),
  state_change_time: z.string(),
  description: z.string().nullable(),
  region: z.string().nullable(),
  delivered: z.boolean(),
  delivery_error: z.string().optional(),
  // True when the Slack post was deliberately not attempted because the
  // transition was not into ALARM. Without this, a suppressed row and a row
  // whose Slack post FAILED are both just `delivered: false` — and these rows
  // are the history the notifications tell you to go read, so the difference
  // between "we chose not to tell you" and "we tried and could not" has to
  // survive in the record.
  suppressed: z.boolean().optional(),
});
export type AlarmNotificationRow = z.infer<typeof AlarmNotificationRowSchema>;

// Quarterly ISO 42001 Clause 9.1 governance rows, stored under the QUARTER#
// pk (see keys.ts's quarterlySignoffSk / quarterlySignoffHistorySk /
// quarterlyNoteSk / quarterlyManifest for the SK conventions). None of these
// schemas declares `expires_at_epoch`, and none should — these rows are
// audit evidence and the events table's TTL is opt-in per row; setting it
// here would silently delete a sign-off or note the way it once destroyed
// suppression audit_trail data. Not `.passthrough()`: an undeclared field is
// silently stripped at parse, so every field the portal reads must be
// declared here.
//
// One schema covers both the current SIGNOFF#<org> row and the immutable
// SIGNOFF#<org>#<signed_at> history row — they carry identical fields; only
// `sk` (and, for history rows, `revision`) differs between them.
// `org` on every QUARTER# row family. Not a bare z.string(): the QUARTER# SK
// scheme cannot represent an org containing '#' unambiguously — api-quarterly.ts
// splits the CURRENT sign-off row from a history row by '#'-segment count, and
// every prefix query in keys.ts mandates a trailing '#' so `rewindio` cannot
// cross-match `rewindio-labs`. keys.ts now throws at the key builders and the
// API validates at its write boundaries; this closes the same invariant at the
// PARSE boundary, so a row already in the table with a '#'-bearing org is
// dropped as malformed by the read path (which warns-and-drops on a failed
// safeParse) rather than rendered as though it belonged to another org.
//
// Deliberately the bare '#' exclusion, not the API's narrower GitHub-login
// charset (lib/org-name.ts): this is a strict narrowing of an existing
// persisted shape, and rejecting a historic row for anything other than the
// one character the key scheme genuinely cannot carry would turn a schema
// tightening into data loss.
const QuarterlyOrgSchema = z.string().regex(/^[^#]+$/);

export const QuarterlySignoffRowSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  quarter: z.string(),
  org: QuarterlyOrgSchema,
  // Authenticated identity of the signer — never client-supplied free text.
  signed_by: z.string(),
  signed_by_role: z.string(),
  // Server-stamped at signing time (handlers, not this schema, own the
  // stamping). A client-supplied timestamp on an attestation is not evidence.
  signed_at: z.string(),
  attestation: z.string(),
  // The REVIEW# row's generated_at at the moment of signing — this is what
  // binds the signature to a specific version of the report, so a later
  // regeneration of the quarter doesn't silently invalidate what was signed.
  report_generated_at: z.string(),
  // 1-based; increments on every re-sign.
  revision: z.number().int().positive(),
});
export type QuarterlySignoffRow = z.infer<typeof QuarterlySignoffRowSchema>;

export const QuarterlyNoteRowSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  quarter: z.string(),
  org: QuarterlyOrgSchema,
  author: z.string(),
  author_role: z.string(),
  // Server-stamped at write time, same discipline as signed_at above.
  created_at: z.string(),
  body: z.string(),
});
export type QuarterlyNoteRow = z.infer<typeof QuarterlyNoteRowSchema>;

// A vertical marker on the portal's time-series charts: "something changed
// here" (a model upgrade, a prompt rewrite, a policy change), so a variance
// on a chart can be read against what caused it. Explanatory ONLY — nothing
// reads these to gate or silence alerting. Drift-alert suppression keys on
// calibration-change-points.ts, and must keep doing so: an annotation is
// created by a triager or inferred from model_versions, and letting either
// silence a drift alert would reopen the diff-vader#223 hole.
//
// `reviewer` is a council slug ('correctness') or '*' for fleet-wide.
// `effective_date` is a UTC YYYY-MM-DD, compared as a string — never as a
// timestamp, so no mixed-precision comparison can creep in.
export const AnnotationKindSchema = z.enum(['manual', 'model_change']);
export type AnnotationKind = z.infer<typeof AnnotationKindSchema>;

export const AnnotationRowSchema = z.object({
  pk: z.literal('ANNOTATION#all'),
  sk: z.string(),
  id: z.string(),
  kind: AnnotationKindSchema,
  reviewer: z.string(),
  effective_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  title: z.string(),
  detail: z.string().optional(),
  url: z.string().optional(),
  // Server-stamped: the caller's email for manual rows, 'system' for
  // model_change rows.
  author: z.string(),
  created_at: z.string(),
});
export type AnnotationRow = z.infer<typeof AnnotationRowSchema>;

export const QuarterlyManifestRowSchema = z.object({
  pk: z.string(),
  sk: z.literal('QUARTER_MANIFEST'),
  quarter: z.string(),
  generated_at: z.string(),
  orgs: z.array(z.object({
    // Same '#'-exclusion as the sign-off/note rows above. This one is the
    // reachable path: the manifest's `orgs` array is written straight from
    // AggregateQuarterlyReviewFn's caller-supplied `event.orgs`, and it is
    // what the sign-off gauge iterates to decide whether a quarter is signed.
    org: QuarterlyOrgSchema,
    reviewers: z.array(z.string()),
  })),
  // Always true: the manifest is written once this generation IS complete,
  // never as an in-progress marker.
  complete: z.literal(true),
  // Present (and true) ONLY when the generation that wrote this manifest was
  // invoked with an EXPLICIT org subset (`{"quarter":"2026-Q2","orgs":[...]}`),
  // rather than over every discovered org. Such a manifest is a truthful
  // record of what that run did, but it is NOT an authoritative contents list
  // for the quarter — an org absent from `orgs` may simply never have been
  // asked for.
  //
  // This matters because `orgs` is what the sign-off gauge's "every manifest
  // org has signed" rule iterates. Without this flag, a subset re-run silently
  // SHRINKS the authoritative list, and the quarter then reads GREEN once the
  // requested subset signs, with the dropped org unreviewed — the exact
  // partially-performed-control state that rule exists to prevent.
  // publish-quarterly-signoff-gauge.ts therefore treats `orgs_subset: true` as
  // non-authoritative and counts the quarter UNSIGNED.
  //
  // Optional, defaulting ABSENT rather than `false`: every manifest row
  // written before this field existed is a full run, and back-filling a
  // literal `false` onto them is not something a schema can do. Absent and
  // `false` mean the same thing to every reader.
  orgs_subset: z.boolean().optional(),
});
export type QuarterlyManifestRow = z.infer<typeof QuarterlyManifestRowSchema>;
